export type AnalyticsParams = Record<string, string | number | boolean | undefined>;
export type ConsentState = 'unknown' | 'accepted' | 'rejected';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const config = {
  provider: import.meta.env.VITE_ANALYTICS_PROVIDER || 'ga4',
  ga4Id: import.meta.env.VITE_GA4_MEASUREMENT_ID || '',
  enabled: import.meta.env.VITE_GA4_ENABLED === 'true',
  debug: import.meta.env.VITE_GA4_DEBUG_MODE === 'true',
  consentMode: import.meta.env.VITE_GA4_CONSENT_MODE !== 'false',
  cookieConsentRequired: import.meta.env.VITE_COOKIE_CONSENT_REQUIRED !== 'false',
};

const consentKey = 'ai_anime_generator_cookie_consent_v1';
let loaded = false;

export const getConsent = (): ConsentState => {
  const saved = localStorage.getItem(consentKey);
  if (saved === 'accepted' || saved === 'rejected') return saved;
  return 'unknown';
};

export const setConsent = (state: Exclude<ConsentState, 'unknown'>) => {
  localStorage.setItem(consentKey, state);
  if (state === 'accepted') loadAnalytics();
  if (window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: state === 'accepted' ? 'granted' : 'denied',
      ad_storage: 'denied',
    });
  }
  track('cookie_consent_update', {
    analytics_storage: state === 'accepted' ? 'granted' : 'denied',
    ad_storage: 'denied',
    source_page: window.location.pathname,
  });
};

const sanitized = (params: AnalyticsParams = {}) => {
  const blocked = /prompt|image|photo|url|email|name|file|path|signed|raw|token|secret|payment|card/i;
  return Object.fromEntries(
    Object.entries(params).filter(([key, value]) => value !== undefined && !blocked.test(key)),
  );
};

export const loadAnalytics = () => {
  if (loaded || !config.enabled || config.provider !== 'ga4' || !config.ga4Id) return;
  if (config.cookieConsentRequired && getConsent() !== 'accepted') return;
  loaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = (...args: unknown[]) => {
    window.dataLayer?.push(args);
  };
  window.gtag('js', new Date());
  if (config.consentMode) {
    window.gtag('consent', 'default', { analytics_storage: 'granted', ad_storage: 'denied' });
  }
  window.gtag('config', config.ga4Id, { send_page_view: false, debug_mode: config.debug });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(config.ga4Id)}`;
  document.head.appendChild(script);
};

export const initAnalytics = () => {
  if (!config.enabled || !config.ga4Id) return;
  if (!config.cookieConsentRequired || getConsent() === 'accepted') loadAnalytics();
};

export const track = (eventName: string, params?: AnalyticsParams) => {
  if (!config.enabled || !config.ga4Id) return;
  if (config.cookieConsentRequired && getConsent() !== 'accepted') return;
  loadAnalytics();
  window.gtag?.('event', eventName, sanitized(params));
};

export const pageView = (params: { page_path: string; page_title: string; page_type: string }) => {
  track('page_view', {
    ...params,
    locale: navigator.language || 'en',
    referrer: document.referrer ? 'present' : 'direct',
    utm_source: new URLSearchParams(window.location.search).get('utm_source') || undefined,
    utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || undefined,
    utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || undefined,
  });
};

export const analyticsConfig = config;
